import React, { useEffect, useMemo, useState } from "react";
import { Button, Chip, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography, ToggleButtonGroup, ToggleButton, Divider, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio } from "@mui/material";
import { toLocalDateInputValue } from "./utils";
import { EmployeeService } from "../../service/employeeService";
import { LocationService } from "../../service/locationService";
import SimpleTimePicker from "../ui/simpleTimePicker";
import SimpleDatePicker from "../ui/simpleDatePicker";

export default function ScheduleDialog({ open, practitionerId: pid, locationId: lid, schedules, makeDefaultWeekly, onSaveDraft, onClose }) {
  const weeklyBase = (pid && lid) ? (schedules.get(`${pid}-${lid}`) || makeDefaultWeekly()) : makeDefaultWeekly();
  const [draft, setDraft] = useState({
    ...weeklyBase,
    _meta: { 
      ...(weeklyBase?._meta || {}), 
      frequency: weeklyBase?._meta?.frequency || 'weekly',
      end: weeklyBase?._meta?.end || { type: 'never' }, // { type: 'never' | 'on' | 'after', date?: 'YYYY-MM-DD', count?: number }
      start: weeklyBase?._meta?.start || toLocalDateInputValue(new Date())
    }
  });

  const [prName, setPrName] = useState("");
  const [locName, setLocName] = useState("");

  useEffect(() => {
    if (open) {
      const latest = (pid && lid) ? (schedules.get(`${pid}-${lid}`) || makeDefaultWeekly()) : makeDefaultWeekly();
      setDraft({
        ...latest,
        _meta: { 
          ...(latest?._meta || {}), 
          frequency: latest?._meta?.frequency || 'weekly',
          end: latest?._meta?.end || { type: 'never' },
          start: latest?._meta?.start || toLocalDateInputValue(new Date())
        }
      });
      // fetch display names
      EmployeeService.getPractitioners().then(res => {
        if (res?.code === 0 && Array.isArray(res.data)) {
          const p = res.data.find(x => x.id === pid);
          setPrName(p?.fullName || `#${pid || ''}`);
        }
      }).catch(() => setPrName(pid ? `#${pid}` : ""));
      LocationService.getLocations().then(res => {
        if (res?.code === 0 && Array.isArray(res.data)) {
          const l = res.data.find(x => x.id === lid);
          setLocName(l?.name || `#${lid || ''}`);
        }
      }).catch(() => setLocName(lid ? `#${lid}` : ""));
    }
  }, [open, pid, lid, schedules, makeDefaultWeekly]);

  const handleSave = () => onSaveDraft({ pid, lid, draft });
  const handleDelete = () => onSaveDraft({ pid, lid, draft: {} , delete: true});
  const days = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  // Minimal validation for recurrence end
  const todayStr = toLocalDateInputValue(new Date());
  const endMeta = draft?._meta?.end || { type: 'never' };
  const endDateError = endMeta.type === 'on'
    ? (!endMeta.date ? 'End date is required' : (endMeta.date < todayStr ? 'End date cannot be in the past' : ''))
    : '';
  const occurrencesError = endMeta.type === 'after'
    ? (!Number.isFinite(Number(endMeta.count)) || Number(endMeta.count) < 1 ? 'Occurrences must be at least 1' : '')
    : '';
  const formValid = !endDateError && !occurrencesError;

  // Helpers for computing preview
  const addDays = (date, days) => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
  };
  const startOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay(); // 0=Sun
    const diff = d.getDate() - day;
    return new Date(d.getFullYear(), d.getMonth(), diff);
  };
  // Parse an input type=date string (YYYY-MM-DD) as a local date (no UTC shift)
  const parseLocalDate = (yyyyMmDd) => {
    if (!yyyyMmDd || typeof yyyyMmDd !== 'string') return new Date();
    const [y, m, d] = yyyyMmDd.split('-').map(Number);
    return new Date(y, (m || 1) - 1, d || 1);
  };
  const formatHuman = (dateObj) => new Date(dateObj).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', weekday: 'short' });

  const seriesEndPreview = useMemo(() => {
    try {
  const meta = draft?._meta || {};
      const freq = meta.frequency || 'weekly';
      const end = meta.end || { type: 'never' };
      const metaStartStr = meta.start || toLocalDateInputValue(new Date());
  let seriesStart = parseLocalDate(metaStartStr);

      // Enabled weekdays
      const enabledDows = [];
      for (let i = 0; i <= 6; i++) {
        if (draft?.[i]?.enabled) enabledDows.push(i);
      }

      // Map frequency to repeatCount
      const repeatCount = (freq === 'biweekly') ? 2 : (freq === 'weekly' ? 1 : 0);

      if (end.type === 'on' && end.date) {
        return `Series end: ${formatHuman(parseLocalDate(end.date))}`;
      }
      if (end.type === 'never') {
        return `Series end: none`;
      }
      if (end.type === 'after') {
        const count = Number(end.count);
        if (!Number.isFinite(count) || count < 1) return '';

        const occWord = count === 1 ? 'occurrence' : 'occurrences';

        if (freq === 'daily') {
          const last = addDays(seriesStart, count - 1);
          return `Series end: ${formatHuman(last)} (after ${count} ${occWord})`;
        }

        if (enabledDows.length === 0) {
          return `Select at least one weekday to compute end date`;
        }

        // Align start to next enabled weekday (inclusive)
        const baseDow = seriesStart.getDay();
        let delta = 0;
        if (!enabledDows.includes(baseDow)) {
          delta = Math.min(...enabledDows.map(dow => (dow - baseDow + 7) % 7));
        }
        seriesStart = addDays(seriesStart, delta);

        const periodWeeks = repeatCount || 1; // 1 for weekly, 2 for biweekly
        const periodDays = periodWeeks * 7;
        const baseWeekStart = startOfWeek(seriesStart);
        let occurrencesFound = 0;
        let period = 0;
        let lastOccurrence = null;
        const sortedDows = [...enabledDows].sort((a,b)=>a-b);
        while (occurrencesFound < count && period < 520) { // safety
          for (const dow of sortedDows) {
            const occDate = addDays(addDays(baseWeekStart, period * periodDays), dow);
            if (occDate < seriesStart) continue;
            occurrencesFound++;
            lastOccurrence = occDate;
            if (occurrencesFound >= count) break;
          }
          period++;
        }
        if (!lastOccurrence) return '';
        return `Series end: ${formatHuman(lastOccurrence)} (after ${count} ${occWord})`;
      }
      return '';
    } catch {
      return '';
    }
  }, [draft]);
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit Schedule</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 1 }}>Practitioner: {prName} — Location: {locName}</Typography>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mr: 1 }}>Frequency:</Typography>
          <ToggleButtonGroup
            size="small"
            color="primary"
            exclusive
            value={draft?._meta?.frequency || 'weekly'}
            onChange={(_, val) => {
              if (!val) return;
              setDraft(prev => ({
                ...prev,
                _meta: { ...(prev._meta || {}), frequency: val }
              }));
            }}
          >
            <ToggleButton value="daily">Daily</ToggleButton>
            <ToggleButton value="weekly">Weekly</ToggleButton>
            <ToggleButton value="biweekly">Bi-weekly</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Frequency will be used to generate recurring availability when connected to the backend.
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <SimpleDatePicker
            label="Start date"
            value={draft?._meta?.start || toLocalDateInputValue(new Date())}
            onChange={(val) => setDraft(prev => ({
              ...prev,
              _meta: { ...(prev._meta || {}), start: val || '' }
            }))}
            helperText="Series will begin on or after this date"
          />
        </Stack>
        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <FormLabel component="legend">Repeat ends</FormLabel>
          <RadioGroup
            row
            value={draft._meta?.end?.type || 'never'}
            onChange={(e) => {
              const type = e.target.value;
              setDraft(prev => ({
                ...prev,
                _meta: { ...(prev._meta || {}), end: type === 'never' ? { type } : (type === 'on' ? { type, date: toLocalDateInputValue(new Date()) } : { type, count: 10 }) }
              }));
            }}
          >
            <FormControlLabel value="never" control={<Radio size="small" />} label="Never" />
            <FormControlLabel value="on" control={<Radio size="small" />} label="On date" />
            <FormControlLabel value="after" control={<Radio size="small" />} label="After" />
          </RadioGroup>
          {draft._meta?.end?.type === 'on' && (
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <TextField
                size="small"
                type="date"
                label="End date"
                value={draft._meta?.end?.date || toLocalDateInputValue(new Date())}
                onChange={(e) => setDraft(prev => ({
                  ...prev,
                  _meta: { ...(prev._meta || {}), end: { ...(prev._meta?.end || {}), type: 'on', date: e.target.value } }
                }))}
                InputLabelProps={{ shrink: true }}
                error={Boolean(endDateError)}
                helperText={endDateError || ' '}
              />
            </Stack>
          )}
          {draft._meta?.end?.type === 'after' && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
              <TextField
                size="small"
                type="number"
                label="Occurrences"
                inputProps={{ min: 1 }}
                value={draft._meta?.end?.count || 10}
                onChange={(e) => setDraft(prev => ({
                  ...prev,
                  _meta: { ...(prev._meta || {}), end: { ...(prev._meta?.end || {}), type: 'after', count: Math.max(1, Number(e.target.value) || 1) } }
                }))}
                error={Boolean(occurrencesError)}
                helperText={occurrencesError || ' '}
              />
              <Typography variant="caption" color="text.secondary">times</Typography>
            </Stack>
          )}
          {!!seriesEndPreview && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {seriesEndPreview}
            </Typography>
          )}
        </FormControl>
        <Divider sx={{ mb: 2 }} />
        <Stack spacing={1}>
          {days.map((d, idx) => (
            <Stack key={d} direction="row" spacing={1} alignItems="center">
              <Chip size="small" label={d} sx={{ width: 48 }} />
              <Checkbox
                checked={!!draft[idx]?.enabled}
                onChange={(e) => setDraft(prev => ({
                  ...prev,
                  [idx]: {
                    ...(prev[idx] || {}),
                    enabled: e.target.checked,
                    ...(e.target.checked ? {
                      start: (prev[idx]?.start || "09:00"),
                      end: (prev[idx]?.end || "17:00")
                    } : {})
                  }
                }))}
              />
              <SimpleTimePicker
                label="Start"
                value={draft[idx]?.start || "09:00"}
                onChange={(val) => setDraft({ ...draft, [idx]: { ...(draft[idx]||{}), start: val || "" } })}
                step={15}
                disabled={!draft[idx]?.enabled}
              />
              <SimpleTimePicker
                label="End"
                value={draft[idx]?.end || "17:00"}
                onChange={(val) => setDraft({ ...draft, [idx]: { ...(draft[idx]||{}), end: val || "" } })}
                step={15}
                minTime={draft[idx]?.start || null}
                disabled={!draft[idx]?.enabled}
              />
            </Stack>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button color="error" onClick={handleDelete}>Delete</Button>
        <Button variant="contained" onClick={handleSave} disabled={!formValid}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}
